import { createAction, handleActions } from "redux-actions";

import config from "appConfig";
import {
  api,
  checkEmptyObject,
  localStorage,
  notification,
  dispatchPermutiveEvent,
  PermutiveEventData
} from "../../services";
import { getSummaryConfig } from "../../dataConfig";
import { LocalStorageKey, PageRoute, PermutiveEvents } from "../../models";
import history, { navigateToLogin } from "../../history";
import {
  favoriteInterface,
  rootReducerInterface,
  UserI
} from "../../typings/interfaces";
import {
  PassedAdI,
  PassedAdListI,
  AdListI
} from "../../typings/routesTypes";

const FAVORITE_TOGGLE = "FAVORITE_TOGGLE";
const FAVORITE_REMOVE = "FAVORITE_REMOVE";
const FAVORITE_LIST_GET = "FAVORITE_LIST_GET";
const FAVORITE_IDS_GET = "FAVORITE_IDS_GET";
const FAVORITE_SYNC = "FAVORITE_SYNC";
const FAVORITE_LOADING_START = "FAVORITE_LOADING_START";
const FAVORITE_LOADING_END = "FAVORITE_LOADING_END";

// Show notification when ad is favorited
const showFavoritedNotification = () => {
  notification.showHiddenAction(
    "Spremljeno u favorite.",
    {
      text: "POGLEDAJ",
      onClick: () => history.push(PageRoute.favorite)
    }
  );
};

// ACTIONS

// Track pending toggles to prevent fast-clicking
const pendingToggles = [];

// Track if favorites are currently being fetched
let isLoadingFavorites = false;

// Actions for tracking loading state
export const startLoadingFavorites = createAction(FAVORITE_LOADING_START);
export const endLoadingFavorites = createAction(FAVORITE_LOADING_END);

// Toggle favorite - using redux-promise pattern with fast-click prevention
export const toggleFavorite = createAction(
  FAVORITE_TOGGLE,
  async (
    ad: PassedAdI,
    favoriteIds: string[] = [],
    favorites: any = [],
    adLists: PassedAdListI,
    user: UserI,
    onComplete?: Function
  ) => {
    const { id: adId, code } = ad;

    // Don't allow toggle while favorites are being fetched
    if (isLoadingFavorites) {
      return null;
    }

    // If ad is already being toggled, do nothing
    if (pendingToggles.includes(adId)) {
      return null;
    }

    // Add to pending array
    pendingToggles.push(adId);

    const isFavorite = favoriteIds.includes(adId);

    if (isFavorite) {
      // Clear Permutive event data
      PermutiveEventData.clear(PermutiveEvents.AdFavorite, code);
      favoriteIds = favoriteIds.filter(id => id !== adId); // eslint-disable-line
      if (favorites) {
        favorites.data = favorites.data.filter(favorite => favorite.id !== adId); // eslint-disable-line
        favorites.count -= 1;
      }
    } else {
      favoriteIds = [...favoriteIds, adId]; // eslint-disable-line
      if (favorites) {
        favorites.data = [...favorites.data, { id: adId, summary: [] }]; // eslint-disable-line
        favorites.count += 1;
      }
    }

    if (checkEmptyObject(user)) {
      try {
        if (isFavorite) {
          await api.Favorite.remove(adId);
        } else {
          await api.Favorite.add(adId);
          showFavoritedNotification();

          // Dispatch Permutive event
          dispatchPermutiveEvent(
            PermutiveEvents.AdFavorite,
            user,
            ad
          );
        }
      } catch (error) {
        // Remove from pending on error
        const index = pendingToggles.indexOf(adId);
        if (index > -1) {
          pendingToggles.splice(index, 1);
        }
        return null;
      }
    } else {
      localStorage.setItem(LocalStorageKey.favorites, ad);
      navigateToLogin();
    }

    // Remove from pending array once endpoint resolves
    const index = pendingToggles.indexOf(adId);
    if (index > -1) {
      pendingToggles.splice(index, 1);
    }

    // Call the callback if provided (for reloading data after deletion)
    if (onComplete) {
      onComplete();
    }

    return {
      favoriteIds,
      favorites,
    };
  }
);

// Remove favorite - always DELETE, used on FavoritePage where all items are favorites
export const removeFavorite = createAction(
  FAVORITE_REMOVE,
  async (
    adId: string,
    favoriteIds: string[] = [],
    favorites: any = null,
    user: UserI
  ) => {
    // Don't allow remove while favorites are being fetched
    if (isLoadingFavorites) {
      return null;
    }

    // If ad is already being toggled, do nothing
    if (pendingToggles.includes(adId)) {
      return null;
    }

    // Add to pending array
    pendingToggles.push(adId);

    // Optimistically update state - always remove
    var updatedFavoriteIds = favoriteIds.filter(function(id) { return id !== adId; });
    var updatedFavorites = favorites;
    if (favorites && favorites.data) {
      updatedFavorites = {
        ...favorites,
        data: favorites.data.filter(function(favorite) { return favorite.id !== adId; }),
        count: favorites.count - 1
      };
    }

    if (checkEmptyObject(user)) {
      try {
        await api.Favorite.remove(adId);
      } catch (error) {
        // Remove from pending on error
        var index = pendingToggles.indexOf(adId);
        if (index > -1) {
          pendingToggles.splice(index, 1);
        }
        return null;
      }
    }

    // Remove from pending array once endpoint resolves
    var index = pendingToggles.indexOf(adId);
    if (index > -1) {
      pendingToggles.splice(index, 1);
    }

    return {
      favoriteIds: updatedFavoriteIds,
      favorites: updatedFavorites,
    };
  }
);

// Get favorite ids (string[])
export const getFavoriteIds = createAction(
  FAVORITE_IDS_GET,
  async (
    user: UserI,
  ) => {
    if (checkEmptyObject(user)) {
      try {
        const adList = await api.Favorite.list();
        const favoriteIds = adList.data.map((ad: any) => ad.id);
        return favoriteIds;
      } catch (error) {
        return null;
      }
    }
  }
);

// Get favorites (AdListI[])
export const getFavorites = createAction(
  FAVORITE_LIST_GET,
  async (
    favoriteIds: string[],
    user: UserI,
    module: string,
    page = 1
  ) => {

    if (checkEmptyObject(user)) {
      try {
        // Set loading flag
        isLoadingFavorites = true;

        let adList: AdListI;
        adList = await api.Favorite.list({
          module,
          page,
          itemPerPage: config.maxAdPerPage
        });

        const data = await Promise.all(
          adList.data.map(async (ad: PassedAdI) => {
            const summaryConfig = await getSummaryConfig(ad.module);

            return api.parseSummaryData(summaryConfig, ad, favoriteIds);
          })
        );

        // Clear loading flag
        isLoadingFavorites = false;

        return {
          count: adList.count,
          page: adList.currentPage,
          data,
          isLoading: false
        };
      } catch (error) {
        // Clear loading flag on error
        isLoadingFavorites = false;
        return { isLoading: false };
      }
    }
    return { isLoading: false };
  }
);

// Triggered when user login
export const syncFavorites = createAction(
  FAVORITE_SYNC,
  async (
    user: UserI,
    callback: Function
  ) => {
    if (checkEmptyObject(user)) {
      const favorite = localStorage.getItem(LocalStorageKey.favorites);
      // Push local favorites to sync with server
      if (favorite) {
        try {
          await api.Favorite.add(favorite.id || favorite); // backward compatibility for old favorite data structure when only id is stored
          dispatchPermutiveEvent(
            PermutiveEvents.AdFavorite,
            user,
            favorite
          );
          localStorage.removeItem(LocalStorageKey.favorites);
          showFavoritedNotification();
          callback && callback(user);
        } catch (error) {
          localStorage.removeItem(LocalStorageKey.favorites);
          callback && callback(user);
        }
      } else {
        callback && callback(user);
      }
    }
  }
);

// SELECTORS
export const selectFavoriteIds = (state: rootReducerInterface) =>
  state.favorite.favoriteIds;
export const selectFavorites = (state: rootReducerInterface) =>
  state.favorite.favorites;
export const selectIsLoadingFavorites = (state: rootReducerInterface) =>
  state.favorite.isLoadingFavorites;


// INITIAL STATE
const initialState: favoriteInterface = {
  favoriteIds: [],
  favorites: null,
  isLoadingFavorites: false,
};

// REDUCERS
const favoriteReducers = {
  toggleFavorite: (state: favoriteInterface, { payload }) => {
    // If payload is null, it means the toggle was blocked (fast-clicking prevention)
    if (!payload) {
      return state;
    }
    return {
      ...state,
      ...payload
    };
  },

  removeFavorite: (state: favoriteInterface, { payload }) => {
    // If payload is null, it means the remove was blocked
    if (!payload) {
      return state;
    }
    return {
      ...state,
      favoriteIds: payload.favoriteIds,
      favorites: payload.favorites
    };
  },

  setFavoriteIds: (state: favoriteInterface, { payload }) => ({
    ...state,
    favoriteIds: !payload ? [] : [...payload]
  }),

  setFavorites: (state: favoriteInterface, { payload }) => {
    if (!payload) {
      return {
        ...state,
        isLoadingFavorites: false
      };
    }
    return {
      ...state,
      favorites: {
        count: payload.count,
        page: payload.page,
        data: payload.data
      },
      isLoadingFavorites: payload.isLoading !== undefined ? payload.isLoading : false
    };
  },

  startLoadingFavorites: (state: favoriteInterface) => ({
    ...state,
    isLoadingFavorites: true
  }),

  endLoadingFavorites: (state: favoriteInterface) => ({
    ...state,
    isLoadingFavorites: false
  })
};

export default handleActions(
  {
    [FAVORITE_TOGGLE]: favoriteReducers.toggleFavorite,
    [FAVORITE_REMOVE]: favoriteReducers.removeFavorite,
    [FAVORITE_IDS_GET]: favoriteReducers.setFavoriteIds,
    [FAVORITE_LIST_GET]: favoriteReducers.setFavorites,
    [FAVORITE_LOADING_START]: favoriteReducers.startLoadingFavorites,
    [FAVORITE_LOADING_END]: favoriteReducers.endLoadingFavorites,
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Favorite/favorite.ducks.ts